import React, { useState, useContext, useEffect, useCallback, useMemo } from 'react';
// import { useSearchParams } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import {
  ChevronDown,
  //  ChevronUp,
  X,
  ArrowLeft,
  ArrowRight,
} from 'lucide-react';
import { DevicesContext } from '../../contexts/DevicesContext';
import { calculateStats } from '../../services/deviceApi';
import YearlyAQICalendar from './DeviceHeatMapYear';
import Chart from 'react-apexcharts';
import HelperFunctions from '../../utils/helperFunctions';
import {
  getUserTimezone,
  convertDateRangeToUtcBounds,
} from '../../utils/timezoneUtils';
// import { getDaysInMonth } from 'date-fns';

interface HeatMapDataNew {
  recordedAt: string;
  parameterValue: number;
}

interface HeatMapSeriesDataPoint {
  x: string;
  y: number | string | null;
}

interface HeatMapSeries {
  name: string;
  data: HeatMapSeriesDataPoint[];
}

interface ColorRange {
  from: number | null;
  to: number | null;
  name: string;
  color: string;
}

interface CategoryStat {
  name: string;
  color: string;
  count: number;
}

interface HeatMapDataNewjs {
  jsDate: Date;
  parameterValue: number;
}

interface DeviceHeatMapProps {
  deviceId: string;
  deviceName: string;
  viewMode: "currentmonth" | "currentyear";
  setViewMode: (_view : "currentmonth" | "currentyear") => void;
}

const DeviceHeatMap: React.FC<DeviceHeatMapProps> = ({
  viewMode,
  setViewMode,
}) => {
  const { id } = useParams<{ id: string }>();
  const theme = document.documentElement.getAttribute("data-theme") as 'light' | 'dark' ?? 'light';
  const deviceId = id;
  const devicesContext = useContext(DevicesContext);
  const [dateRangeHeatMap, setDateRangeHeatMap] = useState<[Date, Date] | null>(
    null
  );
  const userPreferences = JSON.parse(
    localStorage.getItem('userPreferences') || '{}'
  );
  const [showTimeRangeDropdown, setShowTimeRangeDropdown] = useState(false);
  const [showTimeRangeModal, setShowTimeRangeModal] = useState(false);
  const [HeatMapChartOptions, setHeatMapChartOptions] = useState<any>({});
  const [HeatMapChartData, setHeatMapChartData] = useState<HeatMapSeries[]>([]);

  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [monthSelectionTrigger, setMonthSelectionTrigger] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 860);
  const [mobileWidth, setMobileWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 860);
      setMobileWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate mobile heatmap height to keep cells square
  const mobileHeatmapHeight = useMemo(() => {
    const numCols = HeatMapChartData[0]?.data?.length || 30;
    const numRows = HeatMapChartData.length || 12;
    // Chart extends into parent padding via negative margins (-1rem each side)
    // so effective width is full screen width
    const containerWidth = mobileWidth;
    // ApexCharts reserves space for y-axis labels and x-axis labels
    // Adjusted to match actual chart rendering so cells stay square
    const yAxisReserved = 10;
    const xAxisReserved = 35;
    const strokeWidth = 2;
    const drawableWidth = containerWidth - yAxisReserved;
    const cellWidth = (drawableWidth - (numCols + 1) * strokeWidth) / numCols;
    const drawableHeight = numRows * cellWidth + (numRows + 1) * strokeWidth;
    const totalHeight = Math.round(drawableHeight + xAxisReserved);
    return Math.max(totalHeight, 200);
  }, [HeatMapChartData, mobileWidth]);

  if (!devicesContext) {
    throw new Error('useDevices must be used within a DevicesProvider');
  }
  const {
    deviceHeatmapData,
    deviceHeatmapDataMinMax,
    getDeviceHeatmapData,
    getDeviceHeatmapMinMax,
    loading,
  } = devicesContext;
  // console.log('deviceHeatmapDataMinMax', deviceHeatmapDataMinMax);
  // const [viewMode, setViewMode] = useState('currentmonth');

  const stats = calculateStats(deviceHeatmapData);

  // Wrapper function to handle month selection with trigger
  const handleMonthSelection = (
    month: number | ((_prevState: number) => number)
  ) => {
    console.log(month);
    setSelectedMonth(month);
    setMonthSelectionTrigger(prev => prev + 1);
  };

  function getLable() {
    if (viewMode === 'currentmonth') {
      // get name from selected month
      return MONTH_NAMES[selectedMonth - 1] + ', ' + selectedYear.toString();
    } else if (viewMode === 'currentyear') {
      return selectedYear.toString();
    }
  }

  const userTimezone = userPreferences.timezone || getUserTimezone();

  // Helper function to convert date range to UTC timestamps using Luxon (DST-safe)
  const getUtcBoundsForRange = useCallback(
    (start: Date, end: Date): [string, string] => {
      return convertDateRangeToUtcBounds(start, end, userTimezone);
    },
    [userTimezone]
  );

  useEffect(() => {
    const getHeatmapData = async () => {
      try {
        if (!dateRangeHeatMap) return;
        const [fromDateUtc, toDateUtc] = getUtcBoundsForRange(
          dateRangeHeatMap[0],
          dateRangeHeatMap[1]
        );
        await getDeviceHeatmapData(Number(deviceId), fromDateUtc, toDateUtc);
      } catch (error) {
        console.error('Error checking devices:', error);
      }
    };
    const getHeatmapDataMinMax = async () => {
      try {
        if (!dateRangeHeatMap) return;
        if (viewMode === 'currentmonth') {
          await getHeatmapData();
          const [fromDateUtc, toDateUtc] = getUtcBoundsForRange(
            dateRangeHeatMap[0],
            dateRangeHeatMap[1]
          );
          await getDeviceHeatmapMinMax(
            Number(deviceId),
            fromDateUtc,
            toDateUtc
          );
        } else {
          const yearStart = new Date(selectedYear, 0, 1);
          const yearEnd = new Date(selectedYear, 11, 31);
          const [fromDateUtc, toDateUtc] = getUtcBoundsForRange(
            yearStart,
            yearEnd
          );
          await getDeviceHeatmapMinMax(
            Number(deviceId),
            fromDateUtc,
            toDateUtc
          );
        }
      } catch (error) {
        console.error('Error checking devices:', error);
      }
    };
    getHeatmapDataMinMax();
  }, [dateRangeHeatMap, viewMode]);

  useEffect(() => {
    if (selectedMonth) {
      const thismonth = new Date().getMonth() + 1;
      if (selectedMonth != thismonth) {
        const firstDay = new Date(Date.UTC(selectedYear, selectedMonth - 1, 1));
        const lastDay = new Date(Date.UTC(selectedYear, selectedMonth, 0));
        setDateRangeHeatMap([firstDay, lastDay]);
      } else {
        const today = new Date();
        const thirtyDaysAgo = new Date(today);
        thirtyDaysAgo.setDate(today.getDate() - 30);
        setDateRangeHeatMap([thirtyDaysAgo, today]);
      }

      setViewMode(viewMode);
    }
  }, [selectedMonth, monthSelectionTrigger]);

  useEffect(() => {
    const getHeatmapData = async () => {
      if (viewMode === 'currentmonth') return;
      try {
        const yearStart = new Date(selectedYear, 0, 1);
        const yearEnd = new Date(selectedYear, 11, 31);
        const [fromDateUtc, toDateUtc] = getUtcBoundsForRange(
          yearStart,
          yearEnd
        );
        await getDeviceHeatmapData(Number(deviceId), fromDateUtc, toDateUtc);
      } catch (error) {
        console.error('Error checking devices:', error);
      }
    };

    getHeatmapData();
  }, [selectedYear, viewMode]);

  function generateDateRange(endDate: Date, numChartDays: number) {
    const dates = [];

    for (let i = 0; i < numChartDays; i++) {
      const day = new Date(endDate);
      day.setDate(endDate.getDate() - i);
      dates.push(day);
    }
    return dates.reverse();
  }

  const MONTH_NAMES = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  function getDateStrFn(day: Date | string) {
    if (!(day instanceof Date)) {
      day = new Date(day);
    }

    const dd = String(day.getDate()).padStart(2, '0');

    const mmm = MONTH_NAMES[day.getMonth()];

    const yy = String(day.getFullYear()).slice(-2);

    return `${dd}/${mmm}/${yy}`;
  }

  function generateIntervals(start: number, end: number, gap: number) {
    const intervals = [];

    for (let time = start; time < end; time += gap) {
      let intervalEnd = time + gap;

      if (intervalEnd === end) {
        intervalEnd = 0;
      } else if (intervalEnd > end) {
        intervalEnd = end;
      }

      intervals.push([time, intervalEnd]);
    }

    return intervals;
  }

  function getSeriesNameFn(interval: number[]) {
    const [startHour, endHour] = interval;
    // Helper to format an hour in 24-hour format into "H:00 AM/PM".
    function formatHour(hour: number) {
      // Treat 24 as 0 to represent midnight.
      const period = hour >= 12 ? 'PM' : 'AM';
      if (hour === 24) hour = 0;
      let hour12 = hour % 12;
      if (hour12 === 0) hour12 = 12;
      return `${hour12}:00 ${period}`;
    }

    return `${formatHour(startHour)} - ${formatHour(endHour)}`;
  }

  function aggregateDataFn(values: number[]) {
    const length = values.length;
    const sum = values.reduce((sum, curr) => {
      sum += curr;
      return sum;
    }, 0);
    return sum / length;
  }

  function getChartValue(values: HeatMapDataNewjs[]) {
    const aggregatedValue =
      values.length > 0
        ? aggregateDataFn(values.map(item => item.parameterValue))
        : null;
    const yValue = aggregatedValue ? aggregatedValue.toFixed(2) : null;
    return yValue;
  }

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.dropdown-container')) {
        setShowTimeRangeDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    generateHeatMapSeriesAndOptions(deviceHeatmapData, 'AQI Graph');
  }, [deviceHeatmapData, isMobile]);

  const generateHeatMapSeriesAndOptions = function (
    rawData: HeatMapDataNew[],
    chartTitle: string
  ) {
    // Parse UTC timestamps and convert to local timezone
    const parsedData = rawData.map(data => {
      const isoString = data.recordedAt;
      // Ensure UTC timestamps are properly parsed (add Z if missing)
      const normalizedIso =
        isoString && !/[Zz]|[+-]\d{2}:\d{2}$/.test(isoString)
          ? `${isoString}Z`
          : isoString;
      // new Date() automatically converts UTC (with Z) to local timezone
      const jsDate = new Date(normalizedIso ?? '');
      return {
        jsDate,
        ...data,
      };
    });
    if (!dateRangeHeatMap) {
      return;
    }

    // Generate chart dates based on local timezone
    // const getStartOfLocalDay = (date: Date) =>
    //   new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const msInDay = 1000 * 60 * 60 * 24;
    const [dataStartDate, dataEndDate] = dateRangeHeatMap;
    const numChartDays =
      Math.floor((dataEndDate.getTime() - dataStartDate.getTime()) / msInDay) +
      1;
    const chartDates = generateDateRange(dataEndDate, numChartDays);

    // Generate chart dates from actual data dates (in local timezone) or date range
    // let chartDates: Date[] = [];
    // if (parsedData.length > 0) {
    //   // Get min and max dates from actual data (in local timezone)
    //   const localTimes = parsedData.map(item => item.jsDate.getTime());
    //   const minLocalDate = getStartOfLocalDay(new Date(Math.min(...localTimes)));
    //   const maxLocalDate = getStartOfLocalDay(new Date(Math.max(...localTimes)));
    //   const numLocalDays = Math.floor((maxLocalDate.getTime() - minLocalDate.getTime()) / msInDay) + 1;
    //   chartDates = generateDateRange(maxLocalDate, numLocalDays);
    // } else {
    //   // Fallback to date range if no data
    //   const numRangeDays = Math.floor((dataEndDate.getTime() - dataStartDate.getTime()) / msInDay) + 1;
    //   chartDates = generateDateRange(dataEndDate, numRangeDays);
    // }

    // Filter chart dates to only include days that have data
    // const chartDatesWithData = chartDates.filter(date =>
    //   parsedData.some(record => {
    //     const recordLocalDate = getStartOfLocalDay(record.jsDate);
    //     const chartLocalDate = getStartOfLocalDay(date);
    //     return recordLocalDate.getTime() === chartLocalDate.getTime();
    //   })
    // );
    // const effectiveChartDates = chartDatesWithData.length > 0 ? chartDatesWithData : chartDates;
    const xAxisLabels = chartDates.map(date => {
      return getDateStrFn(date);
    });
    const intervalArr = generateIntervals(0, 24, 2);

    const yAxisLabels = intervalArr.map(interval => {
      return getSeriesNameFn(interval);
    });

    function isSameDay(itemDate: Date, day: Date) {
      return (
        itemDate.getFullYear() === day.getFullYear() &&
        itemDate.getMonth() === day.getMonth() &&
        itemDate.getDate() === day.getDate()
      );
    }

    // Filter data for 2-hour intervals based on local timezone
    function filterDataForDayAndInterval(
      day: Date,
      startHour: number,
      endHour: number,
      rawData: HeatMapDataNewjs[]
    ) {
      return rawData.filter(item => {
        const itemDate = item.jsDate; // Already in local timezone

        // Check if the item is on the same day (local timezone)
        if (!isSameDay(itemDate, day)) {
          return false;
        }

        // Get hour in local timezone (already converted from UTC)
        const hour = itemDate.getHours();

        // Handle 22:00-24:00 slot (which wraps to next day)
        if (startHour === 22) {
          return hour >= startHour && hour < 24;
        }

        // For other slots, check if hour falls within the 2-hour range
        return hour >= startHour && hour < endHour;
      });
    }
    const MONTH_INDEX_MAP: Record<string, number> = MONTH_NAMES.reduce(
      (acc, curr, index) => {
        acc[curr] = index;
        return acc;
      },
      {} as Record<string, number>
    );

    function parseDateStr(dateStr: string) {
      const parts = dateStr.split('/');
      if (parts.length !== 3) {
        throw new Error('Invalid date format. Expected format DD/MMM/YY.');
      }

      const [dd, mmm, yy] = parts;

      const month = MONTH_INDEX_MAP[mmm];
      if (month === undefined) {
        throw new Error(`Invalid month abbreviation: ${mmm}`);
      }

      const day = parseInt(dd, 10);
      let year = parseInt(yy, 10);
      year = year < 50 ? 2000 + year : 1900 + year;

      return new Date(year, month, day);
    }

    function isWithinDateRange(
      dateStr: string,
      startStr: string,
      endStr: string
    ) {
      const date = parseDateStr(dateStr);
      const start = parseDateStr(startStr);
      const end = parseDateStr(endStr);

      return date >= start && date <= end;
    }

    function handleDatesOutsideDataRange(
      series: HeatMapSeries[],
      dataStartDate: Date,
      dataEndDate: Date
    ) {
      return series.map(series => {
        const { data = [] } = series;
        const modifiedData = data.map(dataPoint => {
          if (
            isWithinDateRange(
              dataPoint.x,
              getDateStrFn(dataStartDate),
              getDateStrFn(dataEndDate)
            )
          ) {
            return dataPoint;
          }
          return {
            ...dataPoint,
            y: 133705,
          };
        });
        return {
          ...series,
          data: modifiedData,
        };
      });
    }

    function getChartDataAsPerIntervals(options: {
      chartDates: Date[];
      intervalArr: number[][];
      parsedData: HeatMapDataNewjs[];
      getChartValue: (_values: HeatMapDataNewjs[]) => string | null;
      getSeriesNameFn: (_interval: number[]) => string;
      getDateStrFn: (_day: Date | string) => string;
      dataStartDate: Date;
      dataEndDate: Date;
    }) {
      const {
        chartDates,
        intervalArr,
        parsedData,
        getChartValue,
        getSeriesNameFn,
        getDateStrFn,
        dataStartDate,
        dataEndDate,
      } = options;

      const series: HeatMapSeries[] = intervalArr.map((interval: number[]) => ({
        name: getSeriesNameFn(interval),
        data: [] as HeatMapSeriesDataPoint[],
      }));

      chartDates.forEach((day: Date) => {
        const dayStr = getDateStrFn(day);
        intervalArr.forEach((interval: number[], index: number) => {
          const [startHour, endHour] = interval;

          const values = filterDataForDayAndInterval(
            day,
            startHour,
            endHour,
            parsedData
          );
          const yValue = getChartValue(values);
          series[index].data.push({
            x: dayStr,
            y: yValue,
          });
        });
      });
      let modifiedSeriesData = series;
      if (dataStartDate && dataEndDate) {
        modifiedSeriesData = handleDatesOutsideDataRange(
          series,
          dataStartDate,
          dataEndDate
        );
      }
      return modifiedSeriesData;
    }

    const charData = getChartDataAsPerIntervals({
      chartDates,
      intervalArr,
      parsedData,
      dataStartDate,
      dataEndDate,
      getChartValue,
      getSeriesNameFn,
      getDateStrFn,
    });

    // function filterDatesWithNoData(labels: string[], seriesData: HeatMapSeries[]) {
    //   const labelHasValue = new Map<string, boolean>();
    //   labels.forEach(label => labelHasValue.set(label, false));

    //   seriesData.forEach(series => {
    //     series.data.forEach(point => {
    //       if (!labelHasValue.has(point.x)) {
    //         return;
    //       }
    //       const value = point.y;
    //       if (value !== null && value !== 133705) {
    //         labelHasValue.set(point.x, true);
    //       }
    //     });
    //   });

    //   const filteredLabels = labels.filter(label => labelHasValue.get(label) ?? false);
    //   const filteredSeries = seriesData.map(series => ({
    //     ...series,
    //     data: series.data.filter(point => labelHasValue.get(point.x) ?? false),
    //   }));

    //   return {
    //     filteredLabels,
    //     filteredSeries,
    //   };
    // }

    // const { filteredLabels, filteredSeries } = filterDatesWithNoData(
    //   xAxisLabels,
    //   charData
    // );

    // Heatmap Options
    const heatmapOptions = getHeatMapOptions(
      chartTitle,
      xAxisLabels,
      yAxisLabels
    );

    setHeatMapChartData(charData);
    setHeatMapChartOptions(heatmapOptions);
  };

  const colorRanges = [
    { from: 0, to: 50, name: '0-50', color: '#A0ED9F' },
    { from: 50.001, to: 100, name: '51-100', color: '#FFE48A' },
    { from: 100.001, to: 200, name: '101-200', color: '#FFB36E' },
    { from: 200.001, to: 300, name: '201-300', color: '#FF8880' },
    { from: 300.001, to: 400, name: '301-400', color: '#D5A1FE' },
    {
      from: 400.001,
      to: 1000,
      name: '401-500',
      color: '#CB89C4',
    },
    { from: null, to: null, name: 'NA', color: theme == 'light' ? '#F1F1F1' : '#FBFBFB33' },
    {
      from: 133705,
      to: 133705,
      name: 'OUT_OF_DATE_RANGE',
      color: '#F1F1F1',
    },
  ];

  function getTooltip() {
    return function ({
      series,
      seriesIndex,
      dataPointIndex,
      w,
    }: {
      series: any;
      seriesIndex: number;
      dataPointIndex: number;
      w: any;
    }) {
      // console.log(series, seriesIndex, dataPointIndex);
      const value = series[seriesIndex][dataPointIndex];
      const time = w.globals.seriesNames[seriesIndex];
      const date = w.config.xaxis.overwriteCategories[dataPointIndex];

      if (value === null) {
        return `<div style="padding: 8px; background-color: var(--modal-background); border: 1px solid var(--button-border-color); font-size: 12px;">
            <strong>Date:</strong> ${date} <br/>
            <strong>Time:</strong> ${time} <br/>
            <strong>AQI:</strong> N/A
          </div>`;
      }

      if (value === 133705) {
        return `<div style="padding: 8px; background: var(--modal-background); border: 1px solid var(--button-border-color); font-size: 12px;">
            <strong>Date:</strong> ${date} <br/>
            <strong>Time:</strong> ${time} <br/>
            <strong>AQI:</strong> N/A
          </div>`;
      }

      return `<div style="padding: 8px; background: var(--modal-background); border: 1px solid var(--button-border-color); font-size: 12px;">
            <strong>Date:</strong> ${date} <br/>
            <strong>Time:</strong> ${time} <br/>
            <strong>AQI:</strong> ${value || ''}
          </div>`;
    };
  }

  function getTooltipM() {
    return function ({
      series,
      seriesIndex,
      dataPointIndex,
      w,
    }: {
      series: any;
      seriesIndex: number;
      dataPointIndex: number;
      w: any;
    }) {
      // console.log(series, seriesIndex, dataPointIndex);
      const value = series[seriesIndex][dataPointIndex];
      const time = w.globals.seriesNames[seriesIndex];
      const date = w.config.xaxis.overwriteCategories[dataPointIndex];

      if (value === null) {
        return `<div style="padding: 4px; background: var(--modal-background); border: 1px solid var(--button-border-color); font-size: 6px;">
            <strong>Date:</strong> ${date} <br/>
            <strong>Time:</strong> ${time} <br/>
            <strong>AQI:</strong> N/A
          </div>`;
      }

      if (value === 133705) {
        return `<div style="padding: 4px; background: var(--modal-background); border: 1px solid var(--button-border-color); font-size: 6px;">
            <strong>Date:</strong> ${date} <br/>
            <strong>Time:</strong> ${time} <br/>
            <strong>AQI:</strong> N/A
          </div>`;
      }

      return `<div style="padding: 4px; background-color: var(--modal-background); border: 1px solid var(--button-border-color); font-size: 6px;">
            <strong>Date:</strong> ${date} <br/>
            <strong>Time:</strong> ${time} <br/>
            <strong>AQI:</strong> ${value || ''}
          </div>`;
    };
  }

  function getHeatMapOptions(
    chartTitle: string,
    xAxisLabels: string[],
    yAxisLabels: string[]
  ) {
    // xAxisLabels are formatted as "DD/MMM/YY" by getDateStrFn (e.g. "15/Jun/26").
    // `new Date(string)` for this format returns "Invalid Date" on Safari/iOS,
    // so read the month abbreviation directly instead of parsing a Date.
    const getMonthAbbr = (label: string) => label.split('/')[1];

    // Find if the chart is for two months
    const firstMonthChangeIndex = xAxisLabels.findIndex(d => {
      return getMonthAbbr(d) !== getMonthAbbr(xAxisLabels[0]);
    });

    const annotation =
      firstMonthChangeIndex !== -1
        ? [
            {
              x: xAxisLabels[firstMonthChangeIndex],
              position: 'front',
              borderColor: theme =='light' ? '#363636' : '#fff',
              strokeDashArray: 7,
              label: {
                text: new Date(
                  2000,
                  MONTH_NAMES.indexOf(getMonthAbbr(xAxisLabels[firstMonthChangeIndex])),
                  1
                ).toLocaleString('en-US', { month: 'long' }),
                style: {
                  color: theme =='light' ? '#363636' : '#fff',
                  fontSize: '12px',
                  fontFamily: 'DM Sans',
                },
              },
            },
          ]
        : [];

    return {
      chart: {
        type: 'heatmap',
        width: '100%',
        height: '100%',
        // parentHeightOffset: 10,
        // offsetX: isMobile ?-10:0,
        toolbar: { show: false },
        events: {
          // dataPointMouseEnter: onMouseEnter,
          // dataPointMouseLeave: onMouseLeave,
          updated: function () {
            // hideChartLegendDiv();
            // fixChartLegends();
          },
        },
      },
      grid: {
        padding: {
          top: 0,
          right: isMobile ? -5 : 0,
          bottom: 0,
          left: isMobile ?1 : 0,
        },
      },
      // title: {
      //   show: false,
      //   text: chartTitle, // Add your chart title here
      //   align: 'left', // Title alignment (center, left, right)
      //   margin: 0, // Space between title and chart
      //   offsetX: 0, // Horizontal position (you can adjust this to move it left or right)
      //   offsetY: 0, // Vertical position (you can adjust this to move it up or down)
      //   style: {
      //     fontSize: '1.5rem', // Font size of the title
      //     fontWeight: 'bold', // Font weight
      //     fontFamily: 'Roboto Bold', // Font family
      //     color: '#FFFFFF', // Font color
      //   },
      // },
      legend: {
        show: false,
        // position: 'top',
        // showForNullSeries: true,

        // floating: false,
        // offsetY: -35,
        // labels: {
        //   colors: isDark ? "#FFFFFF" : "#333333", // Legend text color
        // },
      },
      stroke: {
        show: true,
        width: 2,
        colors: theme == 'light' ? ["#FFFFFF"] : ["#363636"], 
      },
      plotOptions: {
        heatmap: {
          radius: 0,
          enableShades: false,
          colorScale: {
            inverse: false,
            useFillColorAsStroke: false,
            ranges: colorRanges,
          },
          shadeIntensity: 0.7,
          offsetX: 0,
          offsetY: 0,
        },
      },
      states: {
        hover: {
          filter: {
            type: 'none',
          },
        },
      },
      xaxis: {
        type: 'category',
        overwriteCategories: xAxisLabels,

        tickAmount: isMobile ? 2 : 4,
        labels: {
          show: true,
          formatter: (value: any) => {
            // value is "DD/MMM/YY" (e.g. "05/Aug/25"). Parse the parts directly —
            // `new Date(string)` for this format returns "Invalid Date" on Safari/iOS.
            const [dd, mmm, yy] = String(value).split('/');
            const day = parseInt(dd, 10);
            const month = mmm;
            const year = `20${yy}`;
            if (isMobile) {
              return [`${month} ${day},`, year];
            } else {
              return `${month} ${day},\n${year}`;
            }
          },
          rotate: 0,
          offsetY: isMobile ? 0 : 4,
          rotateAlways: false,
          style: {
            fontSize: isMobile ? '0.5rem' : '0.75rem',
            colors: theme =='light' ? '#363636' : '#fff',
            fontFamily: 'DM Sans',
          },
        },
        tooltip: {
          enabled: false,
        },
      },

      annotations: {
        xaxis: annotation,
        yaxis: [],
      },

      yaxis: {
        type: 'category',
        overwriteCategories: yAxisLabels,
        // offsetx: isMobile ? 6 : 0,
        labels: {
          showDuplicates: true,
          formatter: (value: any, index: number) => {
            try {
              if (!value && !isMobile) {
                return '12 AM';
              }
              if (index % 2 === 0 && isMobile) {
                // skip this label
                return;
              }

              const firstPair = value.split(' - ')[0];
              const cleaned = firstPair.replace(':00', '');
              return cleaned;
            } catch (error) {
              return value;
            }
          },
          offsetY: isMobile ? 10 : 20,
          offsetX: isMobile ? -8 : -6,
          // padding: isMobile ? 5 : 0,
          style: {
            fontSize: isMobile ? '0.5rem' : '0.75rem',
            colors: theme =='light' ? '#363636' : '#fff',
            fontFamily: 'DM Sans',
            // padding: {
            //   left: 5,
            //   right: 5,
            //   top: 0,
            //   bottom: 2,
            // }
          },
        },
        //   axisBorder: {
        //     show: true,
        //     color: '#78909C',
        //     offsetX: 0,
        //     offsetY: 0
        // },
      },
      tooltip: {
        enabled: true,
        custom: isMobile ? getTooltipM() : getTooltip(),
        y: {
          formatter: (val: any) => {
            if (val === null || val === undefined || val === '') {
              return 'NA';
            }
            return val;
          },
        },
      },
      dataLabels: { enabled: false },
    };
  }

  function formatDate(dateString: any): string {
    const date = new Date(dateString);

    // Day, abbreviated month, year
    const options: Intl.DateTimeFormatOptions = {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    };

    return date.toLocaleDateString('en-GB', options);
  }

  type CategoryStats = Record<string, CategoryStat>;

  function categorizeData(
    data: HeatMapDataNew[],
    ranges: ColorRange[]
  ): CategoryStats {
    // Initialize all categories with 0 count
    const result: CategoryStats = ranges.reduce((acc, r) => {
      acc[r.name] = { name: r.name, color: r.color, count: 0 };
      return acc;
    }, {} as CategoryStats);

    for (const item of data) {
      const value = item.parameterValue;

      const range = ranges.find(r => {
        if (r.from === null || r.to === null) return false;
        return value >= r.from && value <= r.to;
      });

      if (range) {
        result[range.name].count++;
      } else if (result['NA']) {
        // fallback to NA if no match
        result['NA'].count++;
      }
    }

    return result;
  }

  const dayscat = categorizeData(deviceHeatmapData, colorRanges);

  const isdropdownDisabled = viewMode === 'currentmonth';

  function gotopreviousMonth() {
    if (selectedMonth === 1) {
      const newYear = selectedYear - 1;
      setSelectedYear(newYear);
      handleMonthSelection(12);
    } else {
      handleMonthSelection(selectedMonth - 1);
    }
  }

  function gotonextMonth() {
    const currentMonth = new Date().getMonth() + 1;
    const currentYear = new Date().getFullYear();

    if (selectedYear === currentYear && selectedMonth === currentMonth) {
      return;
    }

    if (selectedMonth === 12) {
      const newYear = selectedYear + 1;
      setSelectedYear(newYear);
      handleMonthSelection(1);
    } else {
      handleMonthSelection(selectedMonth + 1);
    }
  }



  return (
    <>
      {!isMobile ? (
        <div
          className="bg-transparent rounded-[10px] border border-[var(--button-border-color)] px-6 py-4">
          <div className="grid grid-cols-12 gap-4 w-full mb-4">
            <div className="col-span-12 md:col-span-2 flex justify-between w-full items-center">
              <h2 className="text-2xl font-light text-[var(--color-text)] heatmap-title">
                Heat Map - <span className="font-bold">{getLable()}</span>
              </h2>
            </div>
            <div className="col-span-12 md:col-span-10 flex justify-between w-full items-center">
              <div className="text-sm text-[var(--color-text)] flex items-center space-x-4">
                {viewMode === 'currentmonth' && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => gotopreviousMonth()}
                      className="toggle-button p-3 border-[var(--gradient-page-bottom-color)]"
                    >
                      <ArrowLeft className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => gotonextMonth()}
                      className={`toggle-button p-3 border-[var(--gradient-page-bottom-color)] ${
                        (new Date().getMonth() + 1) === selectedMonth &&
                        new Date().getFullYear() === selectedYear
                          ? 'bg-[#00000024] dark:bg-[#FFFFFF24] pointer-events-none'
                          : ''
                      }`}
                      disabled={
                        (new Date().getMonth() + 1) === selectedMonth &&
                        new Date().getFullYear() === selectedYear
                      }
                    >
                      <ArrowRight className="h-5 w-5" />
                    </button>
                  </div>
                )}
                <div className="legend-container">
                  <div className="legend-item">
                    <span
                      className="w-4 h-4 rounded inline-block"
                      style={{
                        backgroundColor: '#A0ED9F',
                      }}
                    ></span>
                    <span className="legend-label">0-50</span>
                  </div>
                  <div className="legend-item">
                    <span
                      className="w-4 h-4 rounded inline-block"
                      style={{
                        backgroundColor: '#FFE48A',
                      }}
                    ></span>
                    <span className="legend-label">51-100</span>
                  </div>
                  <div className="legend-item">
                    <span
                      className="w-4 h-4 rounded inline-block"
                      style={{
                        backgroundColor: '#FFB36E',
                      }}
                    ></span>
                    <span className="legend-label">101-200</span>
                  </div>
                  <div className="legend-item">
                    <span
                      className="w-4 h-4 rounded inline-block"
                      style={{
                        backgroundColor: '#FF8880',
                      }}
                    ></span>
                    <span className="legend-label">201-300</span>
                  </div>
                  <div className="legend-item">
                    <span
                      className="w-4 h-4 rounded inline-block"
                      style={{
                        backgroundColor: '#D5A1FE',
                      }}
                    ></span>
                    <span className="legend-label">301-400</span>
                  </div>
                  <div className="legend-item">
                    <span
                      className="w-4 h-4 rounded inline-block"
                      style={{
                        backgroundColor: '#CB89C4',
                      }}
                    ></span>
                    <span className="legend-label">401-500</span>
                  </div>
                  <div className="legend-item">
                    <span
                      className="w-4 h-4 rounded inline-block"
                      style={{
                        backgroundColor: theme == 'light' ? '#F1F1F1' : '#FBFBFB33',
                      }}
                    ></span>
                    <span className="legend-label">NA</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setViewMode('currentmonth')}
                  className={`toggle-button heatmap-nav-button ${viewMode === 'currentmonth' ? 'active' : ''}`}
                >
                  Month
                </button>
                <button
                  onClick={() => setViewMode('currentyear')}
                  className={`toggle-button heatmap-nav-button ${viewMode === 'currentyear' ? 'active' : ''}`}
                >
                  Year
                </button>
                <div className="dropdown-container heatmap-year-dropdown">
                  <div
                    className={`dropdown-label heatmap-year-dropdown-label ${
                      isdropdownDisabled
                        ? 'opacity-50 pointer-events-none cursor-not-allowed'
                        : ''
                    }`}
                    onClick={() => {
                      if (!isdropdownDisabled)
                        setShowTimeRangeDropdown(!showTimeRangeDropdown);
                    }}
                  >
                    <div className="dropdown-label-content">
                      <img
                        src={`${process.env.PUBLIC_URL}/SVGIcons/CalenderIcon.svg`}
                        alt="arrow"
                        className="mr-2"
                      />
                      <p>{selectedYear}</p>
                    </div>
                    <ChevronDown strokeWidth={1.5} className={`w-5 h-5 text-[var(--color-sub-title-text)] ${showTimeRangeDropdown ? 'transition-transform rotate-180' : ''}`} />
                  </div>

                  {!isdropdownDisabled && showTimeRangeDropdown && (
                    <div className="dropdown-options heatmap-year-dropdown-options max-h-[25vh] overflow-y-auto">
                      {Array.from({ length: 10 }, (_, i) => {
                        const year = new Date().getFullYear() - i;
                        return (
                          <label key={year} className="dropdown-option">
                            <input
                              type="radio"
                              name="timeRange"
                              value={year}
                              checked={selectedYear === year}
                              onChange={e => {
                                setSelectedYear(Number(e.target.value));
                                setShowTimeRangeDropdown(false);
                              }}
                              className="radio-input"
                            />
                            <span className="radio-label">{year}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="w-full h-full">
            <div className="overflow-hidden">
              {loading ? (
                <div className="heatmap-container loading-container">
                  <div className="h-8 w-8 border-b-2 border-[var(--gradient-button-left-color)] mb-4 animate-spin rounded-full"></div>
                  <p className="text-base text-[var(--color-sub-title-text)]">
                    Loading heatmap data...
                  </p>
                </div>
              ) : deviceHeatmapData ? (
                viewMode === 'currentmonth' ? (
                  <div className="grid grid-cols-12 gap-4 overflow-y-auto scrollbar heatmap-container">
                    <div className="col-span-12 md:col-span-2 space-y-4 text-sm text-gray-700 metric-selectors-heatmap">
                      <div className="grid grid-cols-2">
                        <div className="p-4 border-r border-b border-gray-200">
                          <div
                            className="heatmap-data-value"
                            style={{
                              color: HelperFunctions.GetParameterColor(
                                Math.round(stats?.avg || 0),
                                'AQI'
                              ),
                            }}
                          >
                            {Math.round(stats?.avg || 0)}
                          </div>
                          <div className="heatmap-data-label">Average AQI</div>
                        </div>
                        <div className="p-4 border-b border-gray-200">
                          <div
                            className="heatmap-data-value"
                            style={{
                              color: HelperFunctions.GetParameterColor(
                                Math.round(stats?.max || 0),
                                'AQI'
                              ),
                            }}
                          >
                            {Math.round(stats?.max || 0)}
                          </div>
                          <div className="heatmap-data-label">Max AQI</div>
                        </div>
                        <div className="p-4 border-r border-gray-200">
                          <div
                            className="heatmap-data-value"
                            style={{
                              color: HelperFunctions.GetParameterColor(
                                Math.round(stats?.min || 0),
                                'AQI'
                              ),
                            }}
                          >
                            {Math.round(stats?.min || 0)}
                          </div>
                          <div className="heatmap-data-label">Minimum AQI</div>
                        </div>
                        <div className="p-4">
                          <div className="heatmap-data-value">
                            {deviceHeatmapDataMinMax?.alerts || 0}
                          </div>
                          <div className="heatmap-data-label">Alerts</div>
                        </div>
                      </div>

                      <div>
                        <div className="heatmap-data-min-max-label">
                          Highest Temperature
                        </div>
                        {deviceHeatmapDataMinMax?.temperature?.max?.value ? (
                          <div className="flex justify-between mt-2 align-center">
                            <div className="heatmap-data-valueminmax">
                              {Math.round(
                                deviceHeatmapDataMinMax?.temperature.max.value
                              )}{' '}
                              {deviceHeatmapDataMinMax?.temperature.unit}
                            </div>
                            <span className="inline-block bg-[#C4DBD899] text-gray-700 px-2 py-1 rounded-md text-sm font-medium">
                              {formatDate(
                                deviceHeatmapDataMinMax?.temperature.max
                                  .recordedAt
                              )}
                            </span>
                          </div>
                        ) : (
                          <div className="text-l">--</div>
                        )}
                      </div>
                      <div>
                        <div className="heatmap-data-min-max-label">
                          Lowest Temperature
                        </div>
                        {deviceHeatmapDataMinMax?.temperature?.min?.value ? (
                          <div className="flex justify-between mt-2 align-center">
                            <div className="heatmap-data-valueminmax">
                              {Math.round(
                                deviceHeatmapDataMinMax?.temperature.min.value
                              )}{' '}
                              {deviceHeatmapDataMinMax?.temperature.unit}
                            </div>
                            <span className="inline-block bg-[#C4DBD899] text-gray-700 px-2 py-1 rounded-md text-sm font-medium">
                              {formatDate(
                                deviceHeatmapDataMinMax?.temperature.min
                                  .recordedAt
                              )}
                            </span>
                          </div>
                        ) : (
                          <div className="text-l">--</div>
                        )}
                      </div>

                      {/* Humidity */}
                      <div className="border-t pt-4">
                        <div className="heatmap-data-min-max-label">
                          Highest Humidity
                        </div>
                        {deviceHeatmapDataMinMax?.humidity?.max?.value ? (
                          <div className="flex justify-between mt-2 align-center">
                            <div className="heatmap-data-valueminmax">
                              {Math.round(
                                deviceHeatmapDataMinMax?.humidity.max.value
                              )}{' '}
                              {deviceHeatmapDataMinMax?.humidity.unit}{' '}
                            </div>
                            <span className="inline-block bg-[#C4DBD899] text-gray-700 px-2 py-1 rounded-md text-sm font-medium">
                              {formatDate(
                                deviceHeatmapDataMinMax?.humidity.max.recordedAt
                              )}
                            </span>
                          </div>
                        ) : (
                          <div className="text-l">--</div>
                        )}
                      </div>
                      <div>
                        <div className="heatmap-data-min-max-label">
                          Lowest Humidity
                        </div>
                        {deviceHeatmapDataMinMax?.humidity?.min?.value ? (
                          <div className="flex justify-between mt-2 align-center">
                            <div className="heatmap-data-valueminmax">
                              {Math.round(
                                deviceHeatmapDataMinMax?.humidity.min.value
                              )}{' '}
                              {deviceHeatmapDataMinMax?.humidity.unit}
                            </div>
                            <span className="inline-block bg-[#C4DBD899] text-gray-700 px-2 py-1 rounded-md text-sm font-medium">
                              {formatDate(
                                deviceHeatmapDataMinMax?.humidity.min.recordedAt
                              )}
                            </span>
                          </div>
                        ) : (
                          <div className="text-l">--</div>
                        )}
                      </div>
                    </div>
                    <div className="col-span-12 md:col-span-10 chart-container-heatmap overflow-hidden relative">
                      {deviceHeatmapData?.length === 0 && (
                        <div className="absolute inset-0 z-10 flex items-start justify-center pt-2 pointer-events-none">
                          <p className="rounded-md bg-[var(--card-bg-color)]/80 text-sm text-[var(--color-sub-title-text)] text-center">
                            Data is not available for the selected time period. Please choose a different time period or modify the filters.
                          </p>
                        </div>
                      )}
                      <Chart
                        id="heatchart"
                        options={HeatMapChartOptions}
                        series={HeatMapChartData}
                        type="heatmap"
                        height="100%"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-12 gap-6 overflow-y-auto scrollbar heatmap-container">
                    <div className="col-span-12 md:col-span-2 space-y-4 text-sm metric-selectors-heatmap">
                      <div className="grid grid-cols-2">
                        <div className="p-4 border-r border-b border-gray-200">
                          <div
                            className="heatmap-data-value"
                            style={{
                              color: HelperFunctions.GetParameterColor(
                                Math.round(stats?.avg || 0),
                                'AQI'
                              ),
                            }}
                          >
                            {Math.round(stats?.avg || 0)}
                          </div>
                          <div className="heatmap-data-label">Average AQI</div>
                        </div>
                        <div className="p-4 border-b border-gray-200">
                          <div
                            className="heatmap-data-value"
                            style={{
                              color: HelperFunctions.GetParameterColor(
                                Math.round(stats?.max || 0),
                                'AQI'
                              ),
                            }}
                          >
                            {Math.round(stats?.max || 0)}
                          </div>
                          <div className="heatmap-data-label">Max AQI</div>
                        </div>
                        <div className="p-4 border-r border-gray-200">
                          <div
                            className="heatmap-data-value"
                            style={{
                              color: HelperFunctions.GetParameterColor(
                                Math.round(stats?.min || 0),
                                'AQI'
                              ),
                            }}
                          >
                            {Math.round(stats?.min || 0)}
                          </div>
                          <div className="heatmap-data-label">Minimum AQI</div>
                        </div>
                        <div className="p-4">
                          <div className="heatmap-data-value">
                            {deviceHeatmapDataMinMax?.alerts || 0}
                          </div>
                          <div className="heatmap-data-label">Alerts</div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2">
                        <div className="p-4 border-r border-b border-gray-200">
                          <div
                            className="heatmap-data-value"
                            style={{ color: `${dayscat['0-50'].color}` }}
                          >
                            {dayscat['0-50'].count}
                          </div>
                          <div className="heatmap-data-label">Good Days</div>
                        </div>
                        <div className="p-4 border-b border-gray-200">
                          <div
                            className="heatmap-data-value"
                            style={{ color: `${dayscat['51-100'].color}` }}
                          >
                            {dayscat['51-100'].count}
                          </div>
                          <div className="heatmap-data-label">
                            Moderate Days
                          </div>
                        </div>
                        <div className="p-4 border-r border-b border-gray-200">
                          <div
                            className="heatmap-data-value"
                            style={{ color: `${dayscat['101-200'].color}` }}
                          >
                            {dayscat['101-200'].count}
                          </div>
                          <div className="heatmap-data-label">
                            Unhealthy for Sensitive Groups
                          </div>
                        </div>
                        <div className="p-4 border-b border-gray-200">
                          <div
                            className="heatmap-data-value"
                            style={{ color: `${dayscat['201-300'].color}` }}
                          >
                            {dayscat['201-300'].count}
                          </div>
                          <div className="heatmap-data-label">
                            Unhealthy Days
                          </div>
                        </div>
                        <div className="p-4 border-r border-gray-200">
                          <div
                            className="heatmap-data-value"
                            style={{ color: `${dayscat['301-400'].color}` }}
                          >
                            {dayscat['301-400'].count}
                          </div>
                          <div className="heatmap-data-label">
                            Very Unhealthy Days
                          </div>
                        </div>
                        <div className="p-4">
                          <div
                            className="heatmap-data-value"
                            style={{ color: `${dayscat['401-500'].color}` }}
                          >
                            {dayscat['401-500'].count}
                          </div>
                          <div className="heatmap-data-label">
                            Hazardous Days
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-span-12 md:col-span-10 chart-container-heatmap">
                      <YearlyAQICalendar
                        isMobile={isMobile}
                        year={selectedYear}
                        // deviceId={Number(deviceId)}
                        deviceHeatmapData={deviceHeatmapData}
                        setSelectedMonth={handleMonthSelection}
                        setViewMode={setViewMode}
                      />
                    </div>
                  </div>
                )
              ) : (
                <div className="error-container heatmap-container">
                  <p className="text-black">
                    Data is not available for the selected time period. Please
                    choose a different time period or modify the filters.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <>
          <div>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-l font-light text-[var(--color-text)] heatmap-title">
                Heat Map - <span className="font-bold">{getLable()}</span>
              </h2>
              <div
                className="flex items-center gap-2"
                style={{
                  visibility: viewMode === 'currentyear' ? 'hidden' : undefined,
                }}
              >
                <button
                  onClick={() => gotopreviousMonth()}
                  className={`toggle-button p-2 border-[var(--gradient-page-bottom-color)] ${
                    viewMode === 'currentyear'
                      ? 'bg-[#00000024] dark:bg-[#FFFFFF24] pointer-events-none'
                      : ''
                  }`}
                  disabled={viewMode === 'currentyear'}
                >
                  <ArrowLeft className="h-3 w-3" />
                </button>
                <button
                  onClick={() => gotonextMonth()}
                  className={`toggle-button p-2 border-[var(--gradient-page-bottom-color)] ${
                    (new Date().getMonth() + 1) === selectedMonth &&
                    new Date().getFullYear() === selectedYear
                      ? 'bg-[#00000024] dark:bg-[#FFFFFF24] pointer-events-none'
                      : ''
                  }`}
                  disabled={
                    (new Date().getMonth() + 1) === selectedMonth &&
                    new Date().getFullYear() === selectedYear
                  }
                >
                  <ArrowRight className="h-3 w-3" />
                </button>
              </div>
            </div>
            <div className="flex justify-between mb-4">
              <div className="flex items-center gap-2">
              <button
                onClick={() => setViewMode('currentmonth')}
                className={`toggle-button toggle-button-heatmap ${viewMode === 'currentmonth' ? 'active' : ''}`}
              >
                Month
              </button>
              <button
                onClick={() => setViewMode('currentyear')}
                className={`toggle-button toggle-button-heatmap ${viewMode === 'currentyear' ? 'active' : ''}`}
              >
                Year
              </button>
              </div>
              <div
                className={`dropdown-label dropdown-label-heatmap-year ${
                  isdropdownDisabled
                    ? 'opacity-50 pointer-events-none cursor-not-allowed'
                    : ''
                }`}
                style={{ padding: '0.2rem 0.4rem', gap: '0.25rem', minWidth: '30%', width: 'auto', maxWidth: '40%' }}
                onClick={() => {
                  if (!isdropdownDisabled)
                    setShowTimeRangeModal(!showTimeRangeDropdown);
                }}
              >
                <div className="dropdown-label-content" style={{ gap: '0.25rem' }}>
                  <img
                    src={`${process.env.PUBLIC_URL}/SVGIcons/CalenderIcon.svg`}
                    alt="arrow"
                    style={{
                      height: '12px',
                      width: '12px',
                    }}
                  />
                  <p style={{ fontSize: '0.75rem' }}>{selectedYear}</p>
                </div>
                <ChevronDown strokeWidth={1.5} className="w-4 h-4 text-[var(--color-sub-title-text)]" />
              </div>
            </div>
          </div>

          <div className='flex-1'>
              {loading ? (
                <div className="heatmap-container loading-container">
                  <div className="h-8 w-8 border-b-2 border-[var(--gradient-button-left-color)] mb-4 animate-spin rounded-full"></div>
                  <p className="text-base text-[var(--color-sub-title-text)]">
                    Loading heatmap data...
                  </p>
                </div>
              ) : deviceHeatmapData ? (
                viewMode === 'currentmonth' ? (
                  <div className="heatmap-container">
                    <div className="space-y-4 text-xs metric-selectors-heatmap">
                      <div className="legend-container">
                        <div className="legend-item">
                          <span
                            className="w-2 h-2 rounded-sm inline-block"
                            style={{
                              backgroundColor: '#A0ED9F',
                            }}
                          ></span>
                          <span className="legend-label">0-50</span>
                        </div>
                        <div className="legend-item">
                          <span
                            className="w-2 h-2 rounded-sm inline-block"
                            style={{
                              backgroundColor: '#FFE48A',
                            }}
                          ></span>
                          <span className="legend-label">51-100</span>
                        </div>
                        <div className="legend-item">
                          <span
                            className="w-2 h-2 rounded-sm inline-block"
                            style={{
                              backgroundColor: '#FFB36E',
                            }}
                          ></span>
                          <span className="legend-label">101-200</span>
                        </div>
                        <div className="legend-item">
                          <span
                            className="w-2 h-2 rounded-sm inline-block"
                            style={{
                              backgroundColor: '#FF8880',
                            }}
                          ></span>
                          <span className="legend-label">201-300</span>
                        </div>
                        <div className="legend-item">
                          <span
                            className="w-2 h-2 rounded-sm inline-block"
                            style={{
                              backgroundColor: '#D5A1FE',
                            }}
                          ></span>
                          <span className="legend-label">301-400</span>
                        </div>
                        <div className="legend-item">
                          <span
                            className="w-2 h-2 rounded-sm inline-block"
                            style={{
                              backgroundColor: '#CB89C4',
                            }}
                          ></span>
                          <span className="legend-label">401-500</span>
                        </div>
                        <div className="legend-item">
                          <span
                            className="w-2 h-2 rounded-sm inline-block"
                            style={{
                              backgroundColor: theme == 'light' ? '#F1F1F1' : '#FBFBFB33',
                            }}
                          ></span>
                          <span className="legend-label">NA</span>
                        </div>
                      </div>
                    </div>
                    <div className="chart-container-heatmap-m relative">
                      {deviceHeatmapData?.length === 0 && (
                        <div className="absolute inset-0 z-10 flex items-start justify-center pt-4 mt-2 pointer-events-none"
                        style={{visibility:"hidden"}}>
                          <p className="py-2 rounded-md bg-[var(--card-bg-color)]/80 text-xs text-[var(--color-sub-title-text)] text-center">
                            Data is not available for the selected time period. Please choose a different time period or modify the filters.
                          </p>
                        </div>
                      )}
                      <Chart
                        id="heatchart"
                        options={HeatMapChartOptions}
                        series={HeatMapChartData}
                        type="heatmap"
                        height={mobileHeatmapHeight}
                        width="100%"
                      />
                    </div>
                    <div className="grid grid-cols-4 gap-2 mt-2 mb-2">
                      <div className="bg-transparent shadow-md rounded-lg p-2 flex flex-col items-start space-y-1">
                        <div
                          className="stats-text font-medium text-base md:text-lg"
                          style={{
                            color: HelperFunctions.GetParameterColor(
                              Math.round(stats?.avg || 0),
                              'AQI'
                            ),
                          }}
                        >
                          {Math.round(stats?.avg || 0)}
                        </div>
                        <div className="stats-text text-xs md:text-sm text-[var(--color-sub-title-text)]">
                          Avg AQI
                        </div>
                      </div>

                      <div className="bg-transparent shadow-md rounded-lg p-2 flex flex-col items-start space-y-1">
                        <div
                          className="stats-text font-medium text-base md:text-lg"
                          style={{
                            color: HelperFunctions.GetParameterColor(
                              Math.round(stats?.max || 0),
                              'AQI'
                            ),
                          }}
                        >
                          {Math.round(stats?.max || 0)}
                        </div>
                        <div className="stats-text text-xs md:text-sm text-[var(--color-sub-title-text)]">
                          Max AQI
                        </div>
                      </div>
                      <div className="bg-transparent shadow-md rounded-lg p-2 flex flex-col items-start space-y-1">
                        <div
                          className="stats-text font-medium text-base md:text-lg"
                          style={{
                            color: HelperFunctions.GetParameterColor(
                              Math.round(stats?.min || 0),
                              'AQI'
                            ),
                          }}
                        >
                          {Math.round(stats?.min || 0)}
                        </div>
                        <div className="stats-text text-xs md:text-sm text-[var(--color-sub-title-text)]">
                          Min AQI
                        </div>
                      </div>
                      <div className="bg-transparent shadow-md rounded-lg p-2 flex flex-col items-start space-y-1">
                        <div className="stats-text font-medium text-base md:text-lg text-[var(--color-text)]">
                          {deviceHeatmapDataMinMax?.alerts || 0}
                        </div>
                        <div className="stats-text text-xs md:text-sm text-[var(--color-sub-title-text)]">
                          Alerts
                        </div>
                      </div>
                    </div>
                    <div className="space-y-2 text-xs metric-selectors-heatmap">
                      <div className="grid grid-cols-2 gap-2">
                        <div className="bg-transparent shadow-md rounded-lg p-2 flex flex-col items-start space-y-1">
                          {deviceHeatmapDataMinMax?.temperature?.max?.value ? (
                            <div className="w-full flex justify-between items-center">
                              <div className="inline-block text-[var(--color-text)] py-0 rounded-md time-lable font-medium">
                                {deviceHeatmapDataMinMax?.temperature.max.value.toFixed(
                                  0
                                )}{' '}
                                {deviceHeatmapDataMinMax?.temperature.unit}
                              </div>
                              <span className="inline-block bg-[#C4DBD899] text-gray-700 px-2 py-1 rounded-md time-lable font-medium">
                                {formatDate(
                                  deviceHeatmapDataMinMax?.temperature.max
                                    .recordedAt
                                )}
                              </span>
                            </div>
                          ) : (
                            <div className="text-l">--</div>
                          )}
                          <div className="heatmap-data-min-max-label">
                            Highest Temperature
                          </div>
                        </div>
                        <div className="bg-transparent shadow-md rounded-lg p-2 flex flex-col items-start space-y-1">
                          {deviceHeatmapDataMinMax?.humidity?.max?.value ? (
                            <div className="w-full flex justify-between items-center">
                              <div className="inline-block text-[var(--color-text)] py-0 rounded-md time-lable font-medium">
                                {deviceHeatmapDataMinMax?.humidity.max.value.toFixed(
                                  0
                                )}{' '}
                                {deviceHeatmapDataMinMax?.humidity.unit}{' '}
                              </div>
                              <span className="inline-block bg-[#C4DBD899] text-gray-700 px-2 py-1 rounded-md time-lable font-medium">
                                {formatDate(
                                  deviceHeatmapDataMinMax?.humidity.max
                                    .recordedAt
                                )}
                              </span>
                            </div>
                          ) : (
                            <div className="text-l">--</div>
                          )}
                          <div className="heatmap-data-min-max-label">
                            Highest Humidity
                          </div>
                        </div>
                        <div className="bg-transparent shadow-md rounded-lg p-2 flex flex-col items-start space-y-1">
                          {deviceHeatmapDataMinMax?.temperature?.min?.value ? (
                            <div className="w-full flex justify-between items-center">
                              <div className="inline-block text-[var(--color-text)] py-0 rounded-md time-lable font-medium">
                                {deviceHeatmapDataMinMax?.temperature.min.value.toFixed(
                                  0
                                )}{' '}
                                {deviceHeatmapDataMinMax?.temperature.unit}
                              </div>
                              <span className="inline-block bg-[#C4DBD899] text-gray-700 px-2 py-1 rounded-md time-lable font-medium">
                                {formatDate(
                                  deviceHeatmapDataMinMax?.temperature.min
                                    .recordedAt
                                )}
                              </span>
                            </div>
                          ) : (
                            <div className="text-l">--</div>
                          )}
                          <div className="heatmap-data-min-max-label">
                            Lowest Temperature
                          </div>
                        </div>

                        <div className="bg-transparent shadow-md rounded-lg p-2 flex flex-col items-start space-y-1">
                          {deviceHeatmapDataMinMax?.humidity?.min?.value ? (
                            <div className="w-full flex justify-between items-center">
                              <div className="inline-block text-[var(--color-text)] py-0 rounded-md time-lable font-medium">
                                {deviceHeatmapDataMinMax?.humidity.min.value.toFixed(
                                  0
                                )}{' '}
                                {deviceHeatmapDataMinMax?.humidity.unit}
                              </div>
                              <span className="inline-block bg-[#C4DBD899] text-gray-700 px-2 py-1 rounded-md time-lable font-medium">
                                {formatDate(
                                  deviceHeatmapDataMinMax?.humidity.min
                                    .recordedAt
                                )}
                              </span>
                            </div>
                          ) : (
                            <div className="text-l">--</div>
                          )}
                          <div className="heatmap-data-min-max-label">
                            Lowest Humidity
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="heatmap-container overflow-auto">
                    <div className="mb-2 space-y-2 text-sm text-gray-700 metric-selectors-heatmap">
                      <div className="grid grid-cols-3 gap-2">
                        <div className="bg-transparent shadow-md rounded-lg p-2 flex flex-col items-start">
                          <div
                            className="heatmap-data-value"
                            style={{ color: `${dayscat['0-50'].color}` }}
                          >
                            {dayscat['0-50'].count}
                          </div>
                          <div className="heatmap-data-label">Good Days</div>
                        </div>
                        <div className="bg-transparent shadow-md rounded-lg p-2 flex flex-col items-start">
                          <div
                            className="heatmap-data-value"
                            style={{ color: `${dayscat['51-100'].color}` }}
                          >
                            {dayscat['51-100'].count}
                          </div>
                          <div className="heatmap-data-label">
                            Moderate Days
                          </div>
                        </div>
                        <div className="bg-transparent shadow-md rounded-lg p-2 flex flex-col items-start">
                          <div
                            className="heatmap-data-value"
                            style={{ color: `${dayscat['101-200'].color}` }}
                          >
                            {dayscat['101-200'].count}
                          </div>
                          <div className="heatmap-data-label">
                            Unhealthy for Sensitive Groups
                          </div>
                        </div>
                        <div className="bg-transparent shadow-md rounded-lg p-2 flex flex-col items-start">
                          <div
                            className="heatmap-data-value"
                            style={{ color: `${dayscat['201-300'].color}` }}
                          >
                            {dayscat['201-300'].count}
                          </div>
                          <div className="heatmap-data-label">
                            Unhealthy Days
                          </div>
                        </div>
                        <div className="bg-transparent shadow-md rounded-lg p-2 flex flex-col items-start">
                          <div
                            className="heatmap-data-value"
                            style={{ color: `${dayscat['301-400'].color}` }}
                          >
                            {dayscat['301-400'].count}
                          </div>
                          <div className="heatmap-data-label">
                            Very Unhealthy Days
                          </div>
                        </div>
                        <div className="bg-transparent shadow-md rounded-lg p-2 flex flex-col items-start">
                          <div
                            className="heatmap-data-value"
                            style={{ color: `${dayscat['401-500'].color}` }}
                          >
                            {dayscat['401-500'].count}
                          </div>
                          <div className="heatmap-data-label">
                            Hazardous Days
                          </div>
                        </div>
                      </div>
                      <div className="col-span-12 md:col-span-10 chart-container-heatmap">
                        <YearlyAQICalendar
                          isMobile={isMobile}
                          year={selectedYear}
                          // deviceId={Number(deviceId)}
                          deviceHeatmapData={deviceHeatmapData}
                          setSelectedMonth={handleMonthSelection}
                          setViewMode={setViewMode}
                        />
                      </div>
                      <div className="grid grid-cols-4 gap-2">
                        <div className="bg-transparent shadow-md rounded-lg p-2 flex flex-col items-start space-y-1">
                          <div
                            className="stats-text font-medium text-base md:text-lg"
                            style={{
                              color: HelperFunctions.GetParameterColor(
                                Math.round(stats?.avg || 0),
                                'AQI'
                              ),
                            }}
                          >
                            {Math.round(stats?.avg || 0)}
                          </div>
                          <div className="stats-text text-xs md:text-sm text-[var(--color-sub-title-text)]">
                            Avg AQI
                          </div>
                        </div>

                        <div className="bg-transparent shadow-md rounded-lg p-2 flex flex-col items-start space-y-1">
                          <div
                            className="stats-text font-medium text-base md:text-lg"
                            style={{
                              color: HelperFunctions.GetParameterColor(
                                Math.round(stats?.max || 0),
                                'AQI'
                              ),
                            }}
                          >
                            {Math.round(stats?.max || 0)}
                          </div>
                          <div className="stats-text text-xs md:text-sm text-[var(--color-sub-title-text)]">
                            Max AQI
                          </div>
                        </div>
                        <div className="bg-transparent shadow-md rounded-lg p-2 flex flex-col items-start space-y-1">
                          <div
                            className="stats-text font-medium text-base md:text-lg"
                            style={{
                              color: HelperFunctions.GetParameterColor(
                                Math.round(stats?.min || 0),
                                'AQI'
                              ),
                            }}
                          >
                            {Math.round(stats?.min || 0)}
                          </div>
                          <div className="stats-text text-xs md:text-sm text-[var(--color-sub-title-text)]">
                            Min AQI
                          </div>
                        </div>
                        <div className="bg-transparent shadow-md rounded-lg p-2 flex flex-col items-start space-y-1">
                          <div className="stats-text font-medium text-base md:text-lg text-[var(--color-text)]">
                            {deviceHeatmapDataMinMax?.alerts || 0}
                          </div>
                          <div className="stats-text text-xs md:text-sm text-[var(--color-sub-title-text)]">
                            Alerts
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              ) : (
                <div className="error-container">
                  <p className="text-black">
                    Data is not available for the selected time period. Please
                    choose a different time period or modify the filters.
                  </p>
                </div>
              )}
          </div>

          {isMobile && showTimeRangeModal && (
            <div
              className="mobile-modal-overlay"
              onClick={() => setShowTimeRangeModal(false)}
            >
              <div className="mobile-modal" onClick={e => e.stopPropagation()}>
                <div className="mobile-modal-header">
                  <h3 className="mobile-modal-title">Select Year</h3>
                  <X
                    className="w-5 h-5 text-[var(--color-text)]"
                    onClick={() => setShowTimeRangeModal(false)}
                  />
                </div>
                <div className="mobile-modal-content max-h-[20vh] overflow-y-auto">
                  {Array.from({ length: 10 }, (_, i) => {
                    const year = new Date().getFullYear() - i;
                    return (
                      <label
                        key={year}
                        className="mobile-modal-option"
                      >
                        <input
                          type="radio"
                          name="timeRange"
                          value={year}
                          checked={selectedYear === year}
                          onChange={e => {
                            setSelectedYear(Number(e.target.value));
                            setShowTimeRangeModal(false);
                          }}
                          className="w-4 h-4"
                        />
                        <span className="radio-year">{year}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default DeviceHeatMap;
